import React, { useState, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { 
  FeatherActivity,
  FeatherUser,
  FeatherCalendar,
  FeatherFileText,
  FeatherMessageSquare,
  FeatherBell,
  FeatherEdit2,
  FeatherPlus,
  FeatherTrash2,
  FeatherCheckCircle,
  FeatherClock,
  FeatherUsers
} from '../ui/icons';
import api from '../../services/api';
import './ActivityFeed.css';

const ActivityFeed = ({ companyId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (companyId) {
      fetchActivities();
    }
  }, [companyId]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/partner-companies/${companyId}/activity`);
      setActivities(response.data || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch activity feed');
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (action) => {
    const icons = {
      'created': <FeatherPlus />,
      'updated': <FeatherEdit2 />,
      'status_changed': <FeatherCheckCircle />,
      'communication_logged': <FeatherMessageSquare />,
      'reminder_created': <FeatherBell />,
      'document_uploaded': <FeatherFileText />,
      'deleted': <FeatherTrash2 />
    };
    return icons[action] || <FeatherActivity />;
  };

  const getActivityColor = (action) => {
    const colors = {
      'created': 'success',
      'updated': 'info',
      'status_changed': 'warning',
      'communication_logged': 'info',
      'reminder_created': 'warning',
      'document_uploaded': 'success',
      'deleted': 'danger'
    };
    return colors[action] || 'neutral';
  };

  const formatActivityMessage = (activity) => {
    const { action, details, user } = activity;
    
    const userName = user?.display_name || 'Unknown User';
    
    switch (action) {
      case 'created':
        return `${userName} created this company`;
      
      case 'updated':
        return `${userName} updated company information`;
      
      case 'status_changed':
        const newStatus = details?.new_status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        return `${userName} changed status to "${newStatus}"`;
      
      case 'communication_logged':
        const commType = details?.communication_type?.replace(/\b\w/g, l => l.toUpperCase());
        return `${userName} logged a ${commType} communication`;
      
      case 'reminder_created':
        return `${userName} created a reminder: "${details?.reminder_title}"`;
      
      case 'document_uploaded':
        return `${userName} uploaded a document`;
      
      case 'deleted':
        return `${userName} deleted this company`;
      
      default:
        return `${userName} performed an action`;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) {
        return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="activity-feed">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading activity feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-feed">
      {/* Header */}
      <div className="section-header">
        <div className="header-left">
          <h3>Activity Feed</h3>
          <Badge variant="neutral-tertiary">{activities.length} activities</Badge>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {/* Activities List */}
      {activities.length === 0 ? (
        <div className="empty-state">
          <FeatherActivity size={48} />
          <h4>No Activity Yet</h4>
          <p>Activity will appear here as you interact with this company</p>
        </div>
      ) : (
        <div className="activities-list">
          {activities.map(activity => (
            <div key={activity.id} className="activity-item">
              <div className="activity-icon">
                <div className={`icon-wrapper ${getActivityColor(activity.action)}`}>
                  {getActivityIcon(activity.action)}
                </div>
              </div>
              
              <div className="activity-content">
                <div className="activity-message">
                  {formatActivityMessage(activity)}
                </div>
                
                <div className="activity-meta">
                  <span className="activity-time">
                    <FeatherCalendar size={12} />
                    {formatDate(activity.created_at)}
                  </span>
                  
                  {activity.user && (
                    <span className="activity-user">
                      <FeatherUser size={12} />
                      {activity.user.display_name}
                    </span>
                  )}
                </div>
                
                {/* Additional Details */}
                {activity.details && Object.keys(activity.details).length > 0 && (
                  <div className="activity-details">
                    {activity.details.new_status && (
                      <Badge variant={getActivityColor(activity.action)}>
                        Status: {activity.details.new_status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Badge>
                    )}
                    
                    {activity.details.communication_type && (
                      <Badge variant="info">
                        {activity.details.communication_type.replace(/\b\w/g, l => l.toUpperCase())}
                      </Badge>
                    )}
                    
                    {activity.details.reminder_title && (
                      <Badge variant="warning">
                        Reminder: {activity.details.reminder_title}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityFeed; 